<template>
    <div ref="containerEl" class="relative">
        <div class="flex flex-col items-center space-y-2 lg:space-y-0 lg:space-x-2 lg:flex-row">
            <label :for="`${id}-site-search-type`" class="w-full text-base font-bold text-left lg:w-2/6">
                <span :class="{'text-white': light, 'text-gray-700': !light}">
                    Type of search
                </span>
                <InputSelect
                    :id="`${id}-site-search-type`"
                    class="w-full font-bold py-[12px]"
                    :options="searchTypes"
                    :modelValue="searchType.value"
                    @update:modelValue="updateSearchType"
                    placeholder="Select the type of search"
                    title="Select the type of search"
                    @focusin="searchTypeFocus = true"
                    @focusout="searchTypeFocus = false"
                />
            </label>
            <label :for="id" class="w-full text-base font-bold text-left lg:w-4/6" :class="{'pt-[4px]': searchType.value === 'location'}">
                <span :class="{'text-white': light, 'text-gray-700': !light}">
                    {{ searchType.placeholder }}
                </span>
                <template v-if="searchType.value === 'location'">
                    <FindByLocationInput
                        :id="id"
                        aria-controls="maps_schedules_search_listbox"
                        :aria-activedescendant="results?.length > 0 ? `route_${results[currentFocus]?.id}` : ''"
                        :google-api-key="google.maps_api_key"
                        :google-map-bounds="google.bounds"
                        placeholder="Enter Address, Street or ZIP"
                        :modelValue="searchLocation"
                        @update:modelValue="findByLocation"
                        @change="results.value = []"
                        @keydown.esc="hideResults"
                        @keydown.down="focusNext"
                        @keydown.up="focusPrevious"
                        @keydown.enter="selectFocusedOption"
                    />
                </template>

                <template v-if="searchType.value === 'route'">
                    <SearchInput
                        :id="id"
                        aria-controls="maps_schedules_search_listbox"
                        :aria-activedescendant="results?.length > 0 ? `route_${results[currentFocus]?.id}` : ''"
                        placeholder="Enter Route Name or Number"
                        @keydown.esc="hideResults"
                        @keydown.down="focusNext"
                        @keydown.up="focusPrevious"
                        @keydown.enter="selectFocusedOption"
                        @change="(event) => updateSearchTerm(event.target.value)"
                        :value="searchTerm"
                    />
                </template>

                <template v-if="searchType.value === 'stop'">
                    <SearchInput
                        :id="id"
                        aria-controls="maps_schedules_search_listbox"
                        :aria-activedescendant="results?.length > 0 ? `route_${results[currentFocus]?.id}`: ''"
                        placeholder="Enter Stop Code"
                        @keydown.esc="hideResults"
                        @keydown.down="focusNext"
                        @keydown.up="focusPrevious"
                        @keydown.enter="selectFocusedOption"
                        @change="(event) => updateSearchTerm(event.target.value)"
                        :value="searchTerm"
                    />
                </template>
            </label>
        </div>
        <ul
             role="listbox"
             v-show="!searchTypeFocus && focused && results.length > 0"
             id="maps_schedules_search_listbox"
             class="bg-white rounded-b border absolute w-full z-[70]"
        >
            <li
                v-for="(route, index) in results"
                :key="route.id"
                :value="route.id"
                :id="`route_${route.id}`"
                class="w-full"
                role="option"
                :ref="setOptionRef"
                :aria-selected="index === currentFocus"
                :aria-label="`${route.id} - ${route.longName}`"
                @mousedown="selectOption(index)"
            >
                <div class="flex items-center justify-between w-full px-3 py-4 space-x-2 cursor-pointer group hover:bg-orange-800 hover:text-white group" :class="{'bg-orange-800 text-white outline-2 outline-offset-2 outline-orange-800': index === currentFocus}">
                    <span class="flex items-center font-normal flex-0">
                        <Icon v-if="ICONS[route.group.icon ?? route.group.mode]" class="w-6 h-6 mr-1 group-hover:text-white" :class="{'text-white': index === currentFocus, 'text-primary-300': index !== currentFocus}" :icon="ICONS[route.group.icon ?? route.group.mode]" :aria-label="route.group.mode"></Icon>
                        <Icon v-else :icon="mdiCheck" class="w-5 h-5 mr-2 text-green-500 group-hover:text-white" :class="{'text-white': index === currentFocus, 'text-green-500': index !== currentFocus}" aria-label="No rider alerts"/>
                    </span>
                    <Tag :route="route" />
                    <span class="flex-1 text-left">{{ route.longName }}</span>
                </div>
            </li>
        </ul>
    </div>
</template>

<script>
import { Combobox, ComboboxOption, ComboboxOptions } from '@headlessui/vue';
import { provide } from "vue";
import { usePage } from '@inertiajs/vue3';
import {useFocusWithin, watchDebounced} from "@vueuse/core";
import Fuse from 'fuse.js';
import {defineComponent, inject, nextTick, onBeforeUpdate, onMounted, ref, watch} from 'vue';
import { loadGoogleJavascript, setGoogleApi } from "../../utils/google";
import { ICONS } from "../../utils/icons";
import Icon from "../Icon";
import InputLocation from "../InputLocation";
import InputSearchBox from "../InputSearchBox";
import InputSelect from "../InputSelect";
import FindByLocationInput from './SearchInputs/FindByLocationInput.vue';
import SearchInput from './SearchInputs/SearchInput.vue';
import InputText from "../InputText.vue";
import Tag from "../Tag.vue";
import {useRouteStore} from "../../stores/routes";


export default defineComponent({
    name: 'MapsSchedulesSearch',
    components: {
        Tag,
        InputText,
        SearchInput,
        FindByLocationInput,
        InputLocation,
        InputSelect, InputSearchBox, Combobox, ComboboxOptions, ComboboxOption, Icon
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        light: {
            type: Boolean,
            default: false,
        },
    },
    setup() {
        let allRoutes = [];
        let routesSearcher = [];
        const searchTypes = [
            {
                value: 'route',
                label: 'Find by Route',
                placeholder: 'Enter Route Name or Number',
                debounce: false,
                search: async (value) => {
                    return routesSearcher.search(value).map(result => result.item);
                },
            },
            {
                value: 'stop',
                label: 'Find by Stop',
                placeholder: 'Enter Stop Code',
                debounce: false,
                search: async (stopCode) => await fetchStops({stopCode}),
            },
            {
                value: 'location',
                label: 'Find by Location',
                placeholder: 'Enter Address, Street or ZIP',
                debounce: {debounce: 1000, maxWait: 2000},
                search: async (value) => {
                    if (!value?.location?.lat || !value?.location?.lng) {
                        return [];
                    }

                    return await fetchStops({
                        lat: value.location.lat,
                        lng: value.location.lng,
                        location: value.value,
                    });
                },
            },
        ];

        const api = inject('api');
        const routeStore = useRouteStore();
        const google = usePage().props.global.google;
        const searchType = ref(searchTypes[0]);
        const selectedRoute = ref();
        const searchTerm = ref();
        const results = ref([]);
        const optionRefs = ref([]);
        const currentFocus = ref(-1);
        const searchLocation = ref({
            value: '',
            location: null,
        });

        provide('results', results)

        // Logic to identify whether the focus is inside the whole component, so we can
        // hide the results when it moves outside.
        const containerEl = ref();
        const { focused } = useFocusWithin(containerEl);
        const searchTypeFocus = ref(false);

        const googleJsPromise = loadGoogleJavascript(google.maps_api_key);

        onMounted(async () => {
            await routeStore.initStore();
            googleJsPromise.then(() => {
                setGoogleApi(window.google.maps);
            });
        });

        // FUNCTIONS --------------------------------------

        async function fetchStops({stopCode = null, lat = null, lng = null, location = null}) {
            const response = await api.getStopInfo({
                lat,
                lng,
                stopCode,
                radius: '1000',
                includeRoutes: true,
            });

            if (!response || !response.data) {
                return [];
            }

            const foundRoutes = response.data.reduce((result, {routeIds}) => {
                routeIds.forEach(rId => {
                    const route = allRoutes.find(r => r.id === rId);
                    if (!route || result.hasOwnProperty(route.id)) {
                        return;
                    }

                    result[route.id] = {
                        ...route,
                        searchedStopCode: stopCode,
                        searchedLocation: (lat && lng)
                            ? {lat, lng, location}
                            : null,
                    }
                });

                return result;
            }, {});

            return Object.values(foundRoutes);
        }

        function updateSearchTerm(value) {
            searchTerm.value = value;
        }

        function updateSearchType(value) {
            searchType.value = searchTypes.find(st => st.value === value);
            nextTick(() => {
                searchTerm.value = '';
                searchLocation.value.value = '';
                searchLocation.value.location = null;
            });
        }

        function hideResults() {
            results.value = [];
        }

        async function searchResults() {
            const term = searchTerm.value?.trim();

            if (!searchType.value || !term?.length) {
                results.value = [];
                return;
            }

            if (searchType.value.value === 'location') {
                return;
            }

            results.value = await searchType.value.search(term);

            if(results.value.length > 0) {
                currentFocus.value = 0;
                optionRefs[currentFocus.value]?.focus();
            }
        }

        async function findByLocation({value, location}) {
            searchLocation.value.value = value;
            searchLocation.value.location = location;
            results.value = await searchType.value.search(searchLocation.value);
            updateSearchTerm(value);
            if(results.value.length > 0) {
                currentFocus.value = 0;
                optionRefs[currentFocus.value]?.focus();
            }
        }

        function setOptionRef(el) {
            if (el) {
                optionRefs.value.push(el);
            }
        }

        function focusPrevious(event) {
            event.preventDefault();

            currentFocus.value = (currentFocus.value > 0)
                ? currentFocus.value - 1
                : results.value.length - 1;

            optionRefs[currentFocus.value]?.focus();
        }

        function focusNext(event) {
            event.preventDefault();

            currentFocus.value = (currentFocus.value < results.value.length - 1)
                ? currentFocus.value + 1
                : 0;

            optionRefs[currentFocus.value]?.focus();
        }

        function selectFocusedOption() {
            if (results.value.length === 0) {
                if (searchTerm.value?.trim()?.length > 0) {
                    searchResults();
                }

                return;
            }

            if (currentFocus.value === -1) {
                return;
            }

            selectedRoute.value = results.value[currentFocus.value];
        }

        function selectOption(refId) {
            selectedRoute.value = results.value[refId];
        }

        // WATCHERS --------------------------------------

        // When a route is selected, generate the URL and redirect.
        watch(selectedRoute, (to) => {
            if (searchType.value?.value === 'location') {
                searchLocation.value.value = '';
                searchLocation.value.location = null;
            }

            let href = `/maps-schedules/${to.id}`;

            const queryParams = [];
            if (searchType.value?.value === 'stop' && selectedRoute.value?.searchedStopCode) {
                queryParams.push(`stop=${encodeURIComponent(selectedRoute.value.searchedStopCode)}`);
                queryParams.push('tab=map');
            }
            if (searchType.value?.value === 'location' && selectedRoute.value?.searchedLocation) {
                queryParams.push(`location=${encodeURIComponent(selectedRoute.value.searchedLocation.location)}`);
                queryParams.push('tab=map');
            }

            routeStore.startLoading();
            window.location.href = `${href}?${queryParams.join('&')}`;
        });

        // Create a Fuse instance when route groups change in state store.
        watch(() => routeStore.routeGroups, (rg) => {
            allRoutes = rg.reduce((routes, group) => routes.concat(group.routes), []);
            routesSearcher = new Fuse(allRoutes, {
                threshold: 0.2,
                keys: ['longName', 'shortName']
            });
        }, {immediate: true});

        // When search term changes, search for new results.
        watchDebounced([searchTerm], searchResults, {debounce: 500, maxWait: 1000});

        // Focus first item or remove focus when we have no results.
        watch([results], () => {
            if (results.value.length === 0) {
                currentFocus.value = -1;
            } else {
                currentFocus.value = 0;
            }
        });

        // Reset options references when updating the component, so we don't hold
        // reference in memory for options that are no longer in the DOM.
        onBeforeUpdate(() => optionRefs.value = []);

        return {
            ICONS,
            google,
            results,
            hideResults,
            searchType,
            searchTypes,
            searchTerm,
            selectedRoute,
            searchLocation,
            updateSearchType,
            updateSearchTerm,
            findByLocation,
            setOptionRef,
            focusPrevious,
            focusNext,
            focused,
            currentFocus,
            selectFocusedOption,
            selectOption,
            containerEl,
            searchTypeFocus,
        };
    },
});
</script>
